import { get, post, put, del } from './apiService';

// Ticket enums
export const TicketStatus = {
  Open: 0,
  InProgress: 1,
  Closed: 2,
};

export const TicketPriority = {
  Low: 0,
  Medium: 1,
  High: 2,
};

export const TicketCategory = {
  MatchDispute: 0,
  PaymentIssue: 1,
  BugReport: 2,
  GeneralQuestion: 3,
};

const ticketService = {
  // Get all tickets
  getTickets: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.priority) queryParams.append('priority', filters.priority);
      if (filters.search) queryParams.append('search', filters.search);

      const url = `admin/tickets${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      return await get(url, { withCredentials: false });
    } catch (err) {
      throw new Error(err.message || 'Failed to fetch tickets');
    }
  },

  // Get single ticket by ID
  getTicketById: async (ticketId) => {
    try {
      console.log('Fetching ticket with ID:', ticketId);
      const result = await get(`admin/tickets/${ticketId}`, { withCredentials: false});
      console.log('Ticket data received for ID:', ticketId, result);
      return result;
    } catch (err) {
      console.error('Error fetching ticket with ID:', ticketId, err);
      throw new Error(err.message || 'Failed to fetch ticket');
    }
  },

  // Reply to a ticket
  replyToTicket: async (ticketId, replyData) => {
    try {
      console.log('Sending reply for ticket ID:', ticketId, 'with data:', replyData);
      const result = await post(`admin/tickets/${ticketId}/reply`, replyData, { withCredentials: false});
      console.log('Reply sent successfully for ticket ID:', ticketId, result);
      return result;
    } catch (err) {
      console.error('Error sending reply for ticket ID:', ticketId, err);
      throw new Error(err.message || 'Failed to send reply');
    }
  },

  // Update ticket status
  updateTicketStatus: async (ticketId, status) => {
    try {
      // Convert numeric enum values to string values for API compatibility
      let statusValue = status;

      // If status is a number (enum value), convert it to the corresponding string
      if (typeof status === 'number') {
        switch (status) {
          case TicketStatus.Open:
            statusValue = 'open';
            break;
          case TicketStatus.InProgress:
            statusValue = 'in_progress';
            break;
          case TicketStatus.Closed:
            statusValue = 'closed';
            break;
          default:
            statusValue = 'open'; // Default fallback
        }
      }

      return await put(`admin/tickets/${ticketId}/ticketStatus`, { status: statusValue }, { withCredentials: false});
    } catch (err) {
      throw new Error(err.message || 'Failed to update ticket status');
    }
  },

  // Update ticket priority
  updateTicketPriority: async (ticketId, priority) => {
    try {
      // Convert numeric enum values to string values for API compatibility
      let priorityValue = priority;

      // If priority is a number (enum value), convert it to the corresponding string
      if (typeof priority === 'number') {
        switch (priority) {
          case TicketPriority.Low:
            priorityValue = 'low';
            break;
          case TicketPriority.Medium:
            priorityValue = 'medium';
            break;
          case TicketPriority.High:
            priorityValue = 'high';
            break;
          default:
            priorityValue = 'medium'; // Default fallback
        }
      }

      return await put(`admin/tickets/${ticketId}/priority`, { priority: priorityValue }, { withCredentials: false});
    } catch (err) {
      throw new Error(err.message || 'Failed to update ticket priority');
    }
  },

  // Close ticket
  closeTicket: async (ticketId) => {
    try {
      // Use the status endpoint with the Closed enum value
      return await put(`admin/tickets/${ticketId}/ticketStatus`, { status: TicketStatus.Closed }, { withCredentials: false});
    } catch (err) {
      throw new Error(err.message || 'Failed to close ticket');
    }
  },

  // Delete ticket (if needed)
  deleteTicket: async (ticketId) => {
    try {
      return await del(`admin/tickets/${ticketId}`, { withCredentials: false});
    } catch (err) {
      throw new Error(err.message || 'Failed to delete ticket');
    }
  },
};

export default ticketService;
